import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

/**
 * Three weights, and the distinction is the point.
 *
 * Screens had been writing button classes inline, which meant every action on a row rendered at the
 * same weight: on the forms list, "Copy link", "Analytics", "Archive" and "Delete" were four
 * identical bordered boxes. Nothing said which one you probably want and nothing said which one you
 * cannot undo — so the row read as a wall of equally plausible choices, and the destructive one was
 * the easiest to hit by accident.
 *
 *   primary   — the one thing this screen is for. At most one per view.
 *   secondary — ordinary actions. The default.
 *   ghost     — tertiary; sits inside dense rows without competing.
 *   danger    — destructive, and never styled like the rest.
 */
const VARIANTS = {
  primary:
    "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))] disabled:opacity-50",
  secondary: "border bg-card hover:bg-muted disabled:opacity-50",
  ghost: "text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50",
  danger:
    "border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-50",
} as const;

const SIZES = {
  sm: "px-2 py-1 text-xs gap-1",
  md: "px-3 py-1.5 text-sm gap-1.5",
  // A full-width action on a phone. 16px text and ~44px tall: the WCAG 2.1 AA target size, and
  // small enough type anywhere in a form makes iOS Safari zoom the page on focus.
  lg: "px-4 py-2.5 text-base gap-2",
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
  icon?: LucideIcon;
  children?: ReactNode;
}

export const Button = ({
  variant = "secondary",
  size = "md",
  icon: Icon,
  children,
  className = "",
  type = "button",
  ...rest
}: ButtonProps): JSX.Element => (
  <button
    type={type}
    className={`inline-flex items-center justify-center rounded-md font-medium transition-colors ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
    {...rest}
  >
    {Icon && <Icon aria-hidden className={size === "sm" ? "h-3 w-3" : "h-4 w-4"} />}
    {children}
  </button>
);
